import { ApolloError, useMutation } from '@apollo/client';
import { createOrderInvoiceTemplate } from 'apollo/templates/invoices';
import Loader from 'components/shared/loader/loader.component';
import React, { FC, useState, ChangeEvent, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextField, FormControlLabel, Checkbox, Typography } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { Invoice } from 'models/invoice';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import MissingVatNumberWarning from 'components/shared/missing-vat-number-warning/missing-vat-number-warning.component';

type CreatePerOrderInvoiceDialogProps = {
    orderId: string;
    invNumber: string;
    clinicInvoiceDeliveryMethod: string;
    destInvoiceEmail: string;
    open: boolean;
    shouldSendEmail: boolean;
    onCreateInvoice: (invoice: Invoice) => void;
    onDialogClose: () => void;
};

const CreatePerOrderInvoiceDialog: FC<CreatePerOrderInvoiceDialogProps> = ({
    orderId,
    open,
    shouldSendEmail,
    invNumber,
    clinicInvoiceDeliveryMethod,
    destInvoiceEmail,
    onDialogClose,
    onCreateInvoice,
}: CreatePerOrderInvoiceDialogProps) => {
    const { t: translate } = useTranslation(['common', 'invoices']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [shouldSendEmails, setShouldSendEmails] = useState(false);
    const currentOrganization = currentOrgVar();
    const isManual = currentOrganization?.invoicing?.configuration?.invoiceNumberType === 'MANUAL';

    useEffect(() => {
        setInvoiceNumber(invNumber);
        setShouldSendEmails(shouldSendEmail);
    }, [invNumber, shouldSendEmail]);

    const [createOrderInvoice, { loading }] = useMutation(createOrderInvoiceTemplate, {
        onCompleted: ({ createOrderInvoice: invoice }) => {
            onCreateInvoice(invoice);
            onDialogClose();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleClose = () => {
        onDialogClose();
    };

    const handleEmailCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShouldSendEmails(event.target.checked);
    };

    const handleOk = () => {
        return createOrderInvoice({
            variables: {
                orderId,
                invoiceNumber,
                shouldSendEmails,
            },
        });
    };

    const handleInvoiceNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInvoiceNumber(event.target.value);
    };

    if (loading) {
        return <Loader />;
    }
    return (
        <DHDialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            title={translate('invoices:CONFIRM_CREATE_INVOICE')}
            actions={
                <Button
                    key="ok"
                    disabled={isManual && !invoiceNumber}
                    onClick={handleOk}
                    autoFocus
                    data-test="create-per-order-invoice-dialog.confirm-create-invoice"
                    variant="contained"
                >
                    {translate('invoices:CONFIRM_CREATE_INVOICE')}
                </Button>
            }
        >
            <MissingVatNumberWarning />
            <Typography variant="body1" sx={{ mb: 2 }}>
                {translate('invoices:DIALOG_CONFIRM_INFO')}
            </Typography>
            {isManual ? (
                <TextField
                    required
                    fullWidth
                    id="invoice-number"
                    margin="dense"
                    value={invoiceNumber}
                    onChange={handleInvoiceNumberChange}
                    label={translate('invoices:INVOICE_NUMBER')}
                    data-test="create-per-order-invoice-dialog.invoice-number"
                />
            ) : undefined}
            {clinicInvoiceDeliveryMethod === 'EMAIL' ? (
                <Fragment>
                    <FormControlLabel
                        control={
                            <Checkbox
                                color="secondary"
                                name="email-invoice"
                                checked={shouldSendEmails}
                                onChange={handleEmailCheckbox}
                                data-test="create-per-order-invoice-dialog.send-email-automatically"
                            />
                        }
                        label={translate('invoices:SEND_EMAIL_AUTOMATICALLY')}
                    />
                    {destInvoiceEmail ? (
                        <Fragment>
                            <Typography variant="subtitle2">
                                {translate('invoices:TO')}:{' '}
                                <Typography variant="body1" component="span">
                                    {destInvoiceEmail}
                                </Typography>
                            </Typography>
                        </Fragment>
                    ) : undefined}
                </Fragment>
            ) : undefined}
        </DHDialog>
    );
};

export default CreatePerOrderInvoiceDialog;
